import dateMath from "@elastic/datemath";
import moment from "moment-timezone";
import { formatDays } from "./numberUtils";

import {
  FULL_DATE_FORMAT,
  TABLE_FORMAT_NO_TZ,
  TABLE_FORMAT_FULL,
  DAYS,
  TWO_DAYS_MS,
  ONE_WEEK_MS,
  NEGATE_FILTER,
  TIME_OF_THE_DAY_MAP,
  DAYS_MAP,
  MINUTES_PER_DAY,
} from "Consts";
import { isNullOrUndefined } from "./objectUtils";

const m = moment.tz(moment.tz.guess(true));
const offsetMinutes = m.utcOffset();
const sign = offsetMinutes >= 0 ? "" : "-";
const absOffset = Math.abs(offsetMinutes);
const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
const minutes = String(absOffset % 60).padStart(2, "0");
export const timeZoneOffset = `${sign}${hours}:${minutes}:00`;

const abbrvMapper = {
  AKDT: { displayName: "(GMT-08:00) Alaska", generalAbbv: "AKT" },
  AKST: { displayName: "(GMT-09:00) Alaska", generalAbbv: "AKT" },
  CDT: { displayName: "(GMT-05:00) Central Time", generalAbbv: "CT" },
  CST: { displayName: "(GMT-06:00) Central Time", generalAbbv: "CT" },
  EDT: { displayName: "(GMT-04:00) Eastern Time", generalAbbv: "ET" },
  EST: { displayName: "(GMT-05:00) Eastern Time", generalAbbv: "ET" },
  HST: { displayName: "(GMT-10:00) Hawaii", generalAbbv: "HST" },
  MDT: { displayName: "(GMT-06:00) Mountain Time", generalAbbv: "MT" },
  MST: { displayName: "(GMT-07:00) Mountain Time", generalAbbv: "MT" },
  PDT: { displayName: "(GMT-07:00) Pacific Time", generalAbbv: "PT" },
  PST: { displayName: "(GMT-08:00) Pacific Time", generalAbbv: "PT" },
};

moment.fn.zoneName = function () {
  const abbr = this.zoneAbbr();
  return abbrvMapper[abbr]?.displayName || abbr;
};

export const isEmptyDate = (strDate: string) => {
  if (isNullOrUndefined(strDate) || strDate === "0001-01-01T00:00:00") return true;
  return false;
};

export const formatDateNoTimezone = (strDate, format = TABLE_FORMAT_NO_TZ) => {
  if (isEmptyDate(strDate)) return null;
  return moment(strDate).format(format);
};

export const parseStringServerDate = (strDate, currentUser) => {
  const timezone = moment.tz.guess(); //getTimezone(currentUser);
  return parseStringServerDateWithTimezone(strDate, timezone);
};

export const parseStringServerDateWithTimezone = (strDate, timezone) => {
  if (isEmptyDate(strDate)) return null;
  const m = moment(strDate, FULL_DATE_FORMAT);
  const mtz = moment.tz(m, timezone);
  return mtz;
};

export const formatDate = (strDate, currentUser, format = TABLE_FORMAT_FULL, inputIsUTC = true) => {
  if (inputIsUTC) {
    // convert utc to local
    const mtz = parseStringServerDate(strDate, currentUser);
    const result = mtz ? mtz.format(format || TABLE_FORMAT_FULL) : null;
    return result;
  } else {
    const m = moment(strDate, FULL_DATE_FORMAT);
    return m.format(format);
  }
};

export const formatDateUnixMS = (epochMS, currentUser, format = TABLE_FORMAT_FULL) => {
  const mtz = parseDateUnixMS(epochMS, currentUser);
  return mtz.format(format);
};

const parseDateUnixMS = (epochMS, currentUser) => {
  const timezone = moment.tz.guess(); //getTimezone(currentUser);
  const m = moment(+epochMS);
  const mtz = moment.tz(m, timezone);
  return mtz;
};

/**
 * returns moment object by user's timezone
 * @param {currentUser} currentUser
 */
export const getNow = (currentUser) => {
  const timezone = moment.tz.guess(); //getTimezone(currentUser);
  const m = moment();
  const mtz = moment.tz(m, timezone);
  return mtz;
};

export const datesDiff = (strDate1, strDate2) => {
  if (isEmptyDate(strDate1) || isEmptyDate(strDate2)) return null;
  const m1 = moment(strDate1, FULL_DATE_FORMAT);
  const m2 = moment(strDate2, FULL_DATE_FORMAT);
  return m2.diff(m1);
};

export const getTimeFormatByScale = (start, end) => {
  const startParsed = dateMath.parse(start);
  const endParsed: any = dateMath.parse(end, { roundUp: true });
  const diff: any = moment.duration(endParsed.diff(startParsed));
  const days = endParsed.diff(startParsed, "days");

  if (diff._milliseconds > 172800000) {
    // >48 hours
    return "DD MMM";
  } else if (diff._milliseconds > 7200000 && days > 2) {
    return "HH:mm";
  } else if (days === 2) {
    return "DD MMM HH:mm";
  } else {
    return "HH:mm:ss";
  }
};

export const getCurrentTimezoneOffset = () => {
  // const {
  //   auth: { currentUser },
  // }: any = store.getState();
  const tz = moment.tz.guess(); //getMomentTzFromUserTz(currentUser?.timezone);
  const mtz = moment.tz(moment(), tz);
  return mtz.utcOffset() / 60;
};

const roundDate = (momentDate, diffInMinutes) => {
  let result;
  if (diffInMinutes < 15) {
    return momentDate;
  } else if (diffInMinutes <= 60) {
    result = momentDate.startOf("minute");
  } else if (diffInMinutes <= MINUTES_PER_DAY) {
    const currentMinute = momentDate.minutes();
    const mult = Math.round(currentMinute / 15);
    result = momentDate.startOf("minute").add(mult * 15, "minutes");
  } else {
    // round to the nearset hour
    result = momentDate.add(30, "minutes").startOf("minute");
  }
  return result;
};

export const isRelativeDate = (dateStr) => {
  return typeof dateStr === "string" && dateStr.includes("now");
};

export const convertUIDatesToMomentUserTzDates = (start, end, shouldBeExact = false) => {
  const tz = moment.tz.guess();
  const mtz: any = moment.tz(moment, tz);

  const toRound = !shouldBeExact && isRelativeDate(start) && isRelativeDate(end);
  const nowOnUserTimezone = moment().add(mtz._offset, "minutes").format(FULL_DATE_FORMAT);
  const nowOnUserTimezoneDateObj = moment(nowOnUserTimezone).toDate();
  const startParsed = dateMath.parse(start, { forceNow: nowOnUserTimezoneDateObj });
  const endParsed = dateMath.parse(end, { forceNow: nowOnUserTimezoneDateObj, roundUp: true });
  const diffInMinutes = endParsed?.diff(startParsed, "minutes");
  const roundedStart = toRound ? roundDate(startParsed, diffInMinutes) : startParsed;
  const roundedEnd = toRound ? roundDate(endParsed, diffInMinutes).add(1, "second") : endParsed;

  return {
    end: moment.tz(roundedEnd?.format(FULL_DATE_FORMAT), tz),
    start: moment.tz(roundedStart?.format(FULL_DATE_FORMAT), tz),
  };
};

export const convertUIDatesToMomentUserTzDates2 = (start, end, shouldBeExact = false) => {
  const tz = moment.tz.guess();
  const mtz: any = moment.tz(moment, tz);

  const nowOnUserTimezone = moment().add(mtz._offset, "minutes").format(FULL_DATE_FORMAT);
  const nowOnUserTimezoneDateObj = moment(nowOnUserTimezone).toDate();

  const startParsed = dateMath.parse(start, { forceNow: nowOnUserTimezoneDateObj });
  const endParsed = dateMath.parse(end, { forceNow: nowOnUserTimezoneDateObj, roundUp: true });

  return {
    end: moment.tz(endParsed?.format(FULL_DATE_FORMAT), tz),
    start: moment.tz(startParsed?.format(FULL_DATE_FORMAT), tz),
  };
};

export const getDatesParams = (start, end, utc = true, shouldBeExact = false) => {
  const tzDates = convertUIDatesToMomentUserTzDates2(start, end, shouldBeExact);

  const endDate =
    tzDates.end > moment()
      ? moment().startOf("minute").format(FULL_DATE_FORMAT)
      : tzDates.end.format(FULL_DATE_FORMAT);

  return {
    endDate: endDate,
    startDate: tzDates.start.format(FULL_DATE_FORMAT),
  };
};

export const getDatesParams2 = (start, end, utc = true, shouldBeExact = false) => {
  const tzDates = convertUIDatesToMomentUserTzDates2(start, end, shouldBeExact);

  const endDate =
    tzDates.end > moment()
      ? moment().startOf("minute").format(FULL_DATE_FORMAT)
      : tzDates.end.format(FULL_DATE_FORMAT);

  return {
    endDate: endDate,
    startDate: tzDates.start.format(FULL_DATE_FORMAT),
  };
};

export const getUIDatesAsMoment = (start, end, utc = true, shouldBeExact = false) => {
  const tzDates = convertUIDatesToMomentUserTzDates2(start, end, shouldBeExact);

  if (utc) {
    return {
      endDate: tzDates.end,
      startDate: tzDates.start,
    };
  } else {
    return {
      endDate: tzDates.end,
      startDate: tzDates.start,
    };
  }
};

const getNextHourOcurrence = (minDate, maxDate, fromHour, hoursRange) => {
  const currentHour = minDate.hour();
  const deltaFromNow = fromHour - currentHour;
  const startDate = minDate.clone().add(deltaFromNow, "hours").startOf("hour");
  const toDate = startDate.clone().add(hoursRange, "hours");

  return {
    from: startDate < minDate ? minDate : startDate,
    to: toDate > maxDate ? maxDate : toDate,
  };
};

export const getRanges = (fromDate, untilDate, range, utc = true) => {
  const ranges: any = [];
  let nextDate = fromDate.clone();

  while (nextDate < untilDate) {
    const res = getNextHourOcurrence(nextDate, untilDate, range.startHour, range.hours);
    nextDate = res.to.clone().add(24 - range.hours, "hours");
    if (res.to < res.from) {
      continue;
    }
    const rangeAsString = getDatesParams(res.from, res.to, utc);
    ranges.push(rangeAsString);
  }
  return ranges;
};

const getDayRanges = (fromDate, untilDate, day, utc = true) => {
  const dayRanges: any = [];
  let momentDay = day - 1;
  momentDay = momentDay === 0 ? 7 : momentDay;
  //const dayINeed = 4; // for Thursday
  let startDate;
  if (fromDate.isoWeekday() <= momentDay) {
    startDate = fromDate.clone().isoWeekday(momentDay).startOf("day");
  } else {
    startDate = fromDate.clone().add(1, "weeks").isoWeekday(momentDay).startOf("day");
  }

  while (startDate < untilDate) {
    const endDate = startDate.clone().endOf("day");
    dayRanges.push({
      ...getDatesParams(
        startDate < fromDate ? fromDate.clone() : startDate.clone(),
        endDate > untilDate ? untilDate.clone() : endDate.clone(),
        utc,
        true
      ),
    });
    startDate = startDate.add(1, "weeks");
  }
  return dayRanges;
};

export const getV2DatesParams = (start, end, shouldBeExact = false, fixedTo = null) => {
  const tzDates = convertUIDatesToMomentUserTzDates2(start, end, shouldBeExact);

  return {
    startDateTime: fixedTo
      ? moment(tzDates.end).subtract(fixedTo, "days").startOf("minute").format(FULL_DATE_FORMAT)
      : tzDates.start.startOf("minute").format(FULL_DATE_FORMAT),
    endDateTime: tzDates.end.startOf("minute").format(FULL_DATE_FORMAT),
  };
};

export const mapInterval = (interval, auto) => {
  if (interval === "Auto") return auto;
  if (interval === "1m") return "00:01:00";
  if (interval === "5m") return "00:05:00";
  if (interval === "15m") return "00:15:00";
  if (interval === "30m") return "00:30:00";
  if (interval === "1h") return "01:00:00";
  if (interval === "3h") return "03:00:00";
  if (interval === "6h") return "06:00:00";
  if (interval === "12h") return "12:00:00";
  if (interval === "1d") return "01:00:00:00";
  if (interval === "7d") return "07:00:00:00";
  if (interval === "30d") return "30:00:00:00";
  return auto;
};

export const getDefaultAutoChartInterval = (diff: number, forceAuto: string | null) => {
  if (!diff) return null;
  // two hours
  if (diff <= 7200000) return "00:01:00";
  // four hours
  if (diff > 7200000 && diff <= 14400000) return "00:01:00";

  return diff <= TWO_DAYS_MS ? "01:00:00" : forceAuto || "01:00:00";
};

export const intervalPicker = (interval, DatePickerParams, forceAuto: string | null = null) => {
  const dateRange = getV2DatesParams(DatePickerParams.start, DatePickerParams.end);
  const diff = moment(dateRange.endDateTime).diff(moment(dateRange.startDateTime));
  const defaultAuto = getDefaultAutoChartInterval(diff, forceAuto);
  const autoInterval = diff > ONE_WEEK_MS ? "12:00:00" : defaultAuto;
  return mapInterval(interval, autoInterval);
};

export const addTimestampField = (arr, dateStrField = "date", format = FULL_DATE_FORMAT) => {
  (arr || []).forEach((item) => {
    item._timestamp = moment(item[dateStrField], format).valueOf();
  });
};

export const getExcludeDateTimeFilter = (item, key) => {
  const [_, res] = item.split(NEGATE_FILTER);
  if (key === "days") {
    return `${NEGATE_FILTER}${DAYS_MAP[res]}`;
  }
  return `${NEGATE_FILTER}${TIME_OF_THE_DAY_MAP[res]}`;
};

export const isDayInRange = (start, end, dayString, globalTopFilterObject) => {
  const userTz = convertUIDatesToMomentUserTzDates2(start, end, true);
  const day: any = DAYS.find((d) => d.name === dayString || d.short === dayString);

  // check also that it's not in global filter
  if (!isNullOrUndefined(globalTopFilterObject?.DayOfWeek)) {
    if (!globalTopFilterObject?.DayOfWeek?.includes(day.id)) {
      return false;
    }
  }

  const arr = getDayRanges(userTz.start, userTz.end, day.id);
  return arr.length > 0;
};

export const getDatesAndHoursParams = (start, end, days, timeWindowIds, utc = true) => {
  const mainDates = getDatesParams2(start, end, utc);
  return mainDates;
};

export const formatTimeToAmPm = (timeString: string): string => {
  if (!timeString) return "";

  const timeMoment = moment(timeString, "HH:mm:ss");

  return timeMoment.format("h:mm A");
};

export const isSelectedDateRangeValid = (start, setError) => {
  //@ts-ignore
  const startDate: any = dateMath.parse(start);

  const allowedFrom = moment().subtract(90, "days");
  const allowedTo = moment().add(90, "days");

  let isValid = true;
  if (startDate < allowedFrom) {
    isValid = false;
    setError(`Date range must be less than ${formatDays(90)}`);
  } else if (startDate > allowedTo) {
    isValid = false;
    setError(`Date range must be greater than ${formatDays(90)}`);
  }

  return isValid;
};
